import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Copy, Server, Globe, Shield, RefreshCw, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  ttl: number;
  priority?: number;
}

interface DnsResult {
  domain: string;
  records: DnsRecord[];
  queryTime: number;
  server: string;
}

// This will be moved inside the component to access t function

export const DnsLookupTool: React.FC = () => {
  const { t } = useTranslation();

  const recordTypes = [
    { value: 'A', label: t('tools.dnsLookup.recordTypes.a.label'), description: t('tools.dnsLookup.recordTypes.a.description') },
    { value: 'AAAA', label: t('tools.dnsLookup.recordTypes.aaaa.label'), description: t('tools.dnsLookup.recordTypes.aaaa.description') },
    { value: 'CNAME', label: t('tools.dnsLookup.recordTypes.cname.label'), description: t('tools.dnsLookup.recordTypes.cname.description') },
    { value: 'MX', label: t('tools.dnsLookup.recordTypes.mx.label'), description: t('tools.dnsLookup.recordTypes.mx.description') },
    { value: 'NS', label: t('tools.dnsLookup.recordTypes.ns.label'), description: t('tools.dnsLookup.recordTypes.ns.description') },
    { value: 'TXT', label: t('tools.dnsLookup.recordTypes.txt.label'), description: t('tools.dnsLookup.recordTypes.txt.description') },
    { value: 'SOA', label: t('tools.dnsLookup.recordTypes.soa.label'), description: t('tools.dnsLookup.recordTypes.soa.description') },
    { value: 'PTR', label: t('tools.dnsLookup.recordTypes.ptr.label'), description: t('tools.dnsLookup.recordTypes.ptr.description') },
    { value: 'SRV', label: t('tools.dnsLookup.recordTypes.srv.label'), description: t('tools.dnsLookup.recordTypes.srv.description') },
    { value: 'CAA', label: t('tools.dnsLookup.recordTypes.caa.label'), description: t('tools.dnsLookup.recordTypes.caa.description') },
  ];

  const [domain, setDomain] = useState('');
  const [selectedRecordType, setSelectedRecordType] = useState('A');
  const [loading, setLoading] = useState(false);
  const [dnsResult, setDnsResult] = useState<DnsResult | null>(null);
  const [error, setError] = useState('');
  const [queryHistory, setQueryHistory] = useState<string[]>([]);

  // Mock DNS lookup for demonstration
  const mockDnsLookup = async (domainName: string, recordType: string): Promise<DnsResult> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));
    
    const mockRecords: Record<string, DnsRecord[]> = {
      'A': [
        { type: 'A', name: domainName, value: '93.184.216.34', ttl: 3600 },
        { type: 'A', name: domainName, value: '93.184.216.35', ttl: 3600 }
      ],
      'AAAA': [
        { type: 'AAAA', name: domainName, value: '2606:2800:220:1:248:1893:25c8:1946', ttl: 3600 }
      ],
      'CNAME': [
        { type: 'CNAME', name: `www.${domainName}`, value: domainName, ttl: 300 }
      ],
      'MX': [
        { type: 'MX', name: domainName, value: 'mail.example.com', ttl: 3600, priority: 10 },
        { type: 'MX', name: domainName, value: 'mail2.example.com', ttl: 3600, priority: 20 }
      ],
      'NS': [
        { type: 'NS', name: domainName, value: 'ns1.example.com', ttl: 86400 },
        { type: 'NS', name: domainName, value: 'ns2.example.com', ttl: 86400 }
      ],
      'TXT': [
        { type: 'TXT', name: domainName, value: 'v=spf1 include:_spf.example.com ~all', ttl: 300 },
        { type: 'TXT', name: domainName, value: 'google-site-verification=abc123def456', ttl: 300 }
      ],
      'SOA': [
        { type: 'SOA', name: domainName, value: 'ns1.example.com admin.example.com 2023120101 3600 1800 604800 86400', ttl: 86400 }
      ],
      'SRV': [
        { type: 'SRV', name: `_sip._tcp.${domainName}`, value: '10 5 5060 sip.example.com', ttl: 3600 }
      ],
      'CAA': [
        { type: 'CAA', name: domainName, value: '0 issue "letsencrypt.org"', ttl: 3600 }
      ]
    };

    return {
      domain: domainName,
      records: mockRecords[recordType] || [],
      queryTime: Math.round(50 + Math.random() * 200),
      server: '8.8.8.8'
    };
  };

  const handleLookup = async () => {
    if (!domain.trim()) {
      setError(t('tools.dnsLookup.enterDomainError'));
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;
    if (!domainRegex.test(domain.trim())) {
      setError(t('tools.dnsLookup.invalidDomainError'));
      return;
    }

    setLoading(true);
    setError('');
    setDnsResult(null);

    try {
      const result = await mockDnsLookup(domain.trim(), selectedRecordType);
      setDnsResult(result);
      
      // Add to history
      setQueryHistory(prev => {
        const newHistory = [domain.trim(), ...prev.filter(d => d !== domain.trim())];
        return newHistory.slice(0, 10); // Keep last 10 queries
      });
    } catch (err) {
      setError(t('tools.dnsLookup.lookupError'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const lookupAllRecords = async () => {
    if (!domain.trim()) {
      setError(t('tools.dnsLookup.enterDomainError'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const allResults: DnsRecord[] = [];
      
      // Query multiple record types
      for (const recordType of ['A', 'AAAA', 'CNAME', 'MX', 'NS', 'TXT']) {
        try {
          const result = await mockDnsLookup(domain.trim(), recordType);
          allResults.push(...result.records);
        } catch (err) {
          // Continue with other record types if one fails
        }
      }

      setDnsResult({
        domain: domain.trim(),
        records: allResults,
        queryTime: Math.round(200 + Math.random() * 300),
        server: '8.8.8.8'
      });
    } catch (err) {
      setError(t('tools.dnsLookup.comprehensiveLookupError'));
    } finally {
      setLoading(false);
    }
  };

  const formatTTL = (ttl: number): string => {
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m`;
    if (ttl < 86400) return `${Math.floor(ttl / 3600)}h`;
    return `${Math.floor(ttl / 86400)}d`;
  };

  return (
    <div className="space-y-6">
      {/* DNS Lookup Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server className="w-5 h-5" />
            <span>{t('tools.dnsLookup.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  label={t('tools.dnsLookup.domainName')}
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder={t('tools.dnsLookup.domainPlaceholder')}
                  onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.dnsLookup.recordType')}
                </label>
                <select
                  value={selectedRecordType}
                  onChange={(e) => setSelectedRecordType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {recordTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleLookup}
                disabled={loading}
                variant="primary"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? t('tools.dnsLookup.lookingUp') : t('tools.dnsLookup.lookup')}
              </Button>
              <Button
                onClick={lookupAllRecords}
                disabled={loading}
                variant="outline"
              >
                <Globe className="w-4 h-4 mr-2" />
                {t('tools.dnsLookup.lookupAllRecords')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Record Type Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.dnsLookup.dnsRecordTypes')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recordTypes.map((type) => (
              <div
                key={type.value}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedRecordType === type.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setSelectedRecordType(type.value)}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {type.label}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {type.description}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* DNS Results */}
      {dnsResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.dnsLookup.dnsRecordsFor')} {dnsResult.domain}</span>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{dnsResult.queryTime}ms</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Server className="w-4 h-4" />
                  <span>{dnsResult.server}</span>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dnsResult.records.length > 0 ? (
              <div className="space-y-3">
                {dnsResult.records.map((record, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded text-sm font-medium">
                          {record.type}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {record.name}
                        </span>
                        {record.priority && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded text-sm">
                            {t('tools.dnsLookup.priority')}: {record.priority}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {t('tools.dnsLookup.ttl')}: {formatTTL(record.ttl)}
                        </span>
                        <Button
                          onClick={() => copyToClipboard(record.value)}
                          size="sm"
                          variant="outline"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      {record.value}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('tools.dnsLookup.noRecordsFound', { recordType: selectedRecordType, domain: dnsResult.domain })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Query History */}
      {queryHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Queries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {queryHistory.map((historyDomain, index) => (
                <Button
                  key={index}
                  onClick={() => setDomain(historyDomain)}
                  variant="outline"
                  size="sm"
                >
                  {historyDomain}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
